/**
 * «Condividi via email» della scheda ticket: testo semplice del ticket da
 * mettere in un `mailto:` (o da copiare). Funzioni pure, nessun accesso al DOM.
 *
 * Il corpo usa `\r\n` (RFC 6068: gli a capo nel `body` di un mailto sono CRLF)
 * e testo semplice: l'eventuale HTML dei messaggi diventa testo, le email in
 * testo semplice hanno le righe riunite come nel thread (`unwrapEmailText`).
 * Le note interne entrano SOLO se richiesto e solo se il ticket caricato le ha.
 * Con `links` (link pubblici firmati generati dal backend) ogni allegato
 * diventa «nome – link». Nei dettagli, i «Dati della pratica» compilati (canale
 * «Documento Unico») con le etichette del ticket.
 */
import { formatDate, PRIORITY_LABELS, STATUS_LABELS } from '../constants';
import { emailMessageFlags, isEmailNote, unwrapEmailText } from '../v3/emailText';
import { ticketShareUrl, type ShareableTicket } from '../shareTicket';
import { righeDatiPratica } from '../campiPratica';
import type { ShareLink, ShareLinkRequest, TicketDetail } from '../../../services/supportApi';

export type ShareEmailContent = 'first' | 'last' | 'all';

export interface ShareEmailOptions {
  content: ShareEmailContent;
  includeNotes: boolean;
  includeDetails: boolean;
  /** Origine del sito per il link universale (`window.location.origin`). */
  origin: string;
  /** Link pubblici agli allegati: chi ce l'ha diventa «nome – link». */
  links?: ShareLink[];
  /** Scadenza ISO dei link (riga finale), null = senza scadenza. */
  linksExpireAt?: string | null;
  /**
   * Anteprima: ogni allegato collegabile mostra `LINK_PLACEHOLDER` al posto
   * del link (i link veri si generano solo all'invio o alla copia).
   */
  linksPlaceholder?: boolean;
}

/** Segnaposto dei link nell'anteprima, prima della generazione. */
export const LINK_PLACEHOLDER = '[link generato all’invio]';

/** Il ticket così come lo ha la scheda: tutto facoltativo oltre all'id. */
export type ShareEmailTicket = ShareableTicket &
  Partial<
    Pick<
      TicketDetail,
      | 'status'
      | 'priority'
      | 'groupId'
      | 'assignees'
      | 'associateSnapshot'
      | 'publicThread'
      | 'internalNotes'
      | 'createdAt'
      | 'requester'
      | 'origin'
      | 'history'
      | 'viewerRole'
      | 'campiExtra'
      | 'campiRichiesta'
    >
  >;

/** Oltre questa lunghezza del corpo alcuni programmi di posta troncano il mailto. */
export const MAILTO_SOFT_LIMIT = 1800;

const CRLF = '\r\n';
const SEPARATOR = '----------------------------------------';

const ENTITIES: Record<string, string> = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

/** Code point Unicode valido (niente zero, oltre 0x10FFFF o surrogati isolati). */
const isValidCodePoint = (n: number): boolean =>
  Number.isInteger(n) && n > 0 && n <= 0x10ffff && (n < 0xd800 || n > 0xdfff);

const looksLikeHtml = (s: string): boolean => /<\/?[a-z][a-z0-9]*(?:\s[^>]*)?\/?>/i.test(s);

/** HTML → testo semplice (a capo per blocchi e <br>, «- » per le voci di elenco, entità decodificate). */
export const htmlToText = (html: string): string => {
  if (!html || !looksLikeHtml(html)) return html || '';
  return html
    .replace(/\r\n?/g, '\n')
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote|ul|ol|table)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, code: string) => {
      if (code[0] === '#') {
        const n = code[1] === 'x' || code[1] === 'X' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
        // Fuori range o surrogato: entità lasciata com'è (fromCodePoint lancerebbe RangeError).
        return isValidCodePoint(n) ? String.fromCodePoint(n) : m;
      }
      const v = ENTITIES[code.toLowerCase()];
      return v === undefined ? m : v;
    })
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Note condivisibili: le eliminate no (il backend non le manda piu; robustezza). */
const liveNotes = (ticket: ShareEmailTicket) =>
  (Array.isArray(ticket.internalNotes) ? ticket.internalNotes : []).filter((n) => n && !n.deletedAt);

/** Le note sono nel ticket caricato (vista operatore del canale o admin) e ce n'è almeno una. */
export const canShareNotes = (ticket: ShareEmailTicket): boolean => liveNotes(ticket).length > 0;

/** Oggetto su una riga: a capo e tabulazioni diventano spazi. */
export const shareEmailSubject = (ticket: ShareEmailTicket): string =>
  `Ticket #${ticket.ticketNumber || ticket._id} – ${ticket.subject || ''}`.replace(/[\r\n\t]+/g, ' ').trimEnd();

interface ShareAttachment {
  name: string;
  index: number;
}

interface ShareItem {
  label: string;
  author: string;
  sentAt: string;
  body: string;
  /** Id del messaggio o della nota: serve ad agganciare i link agli allegati. */
  messageId?: string;
  attachments: ShareAttachment[];
  time: number;
  rank: number;
  index: number;
}

const timeOf = (iso: string): number => {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const textOf = (body: string, isEmail: boolean): string => {
  const plain = htmlToText(body || '');
  return isEmail ? unwrapEmailText(plain) : plain;
};

const collectItems = (ticket: ShareEmailTicket, includeNotes: boolean): ShareItem[] => {
  const thread = ticket.publicThread || [];
  const flags = emailMessageFlags(thread, { origin: ticket.origin, history: ticket.history });
  const messages: ShareItem[] = thread.map((m, i) => {
    const fromRequester = m.authorType === 'associate' || m.authorType === 'guest';
    return {
      label: fromRequester ? 'Richiedente' : 'Canale',
      author: m.authorName || (fromRequester ? 'Richiedente' : 'Canale'),
      sentAt: m.sentAt,
      body: textOf(m.body, Boolean(flags[i])),
      messageId: m._id,
      attachments: (m.attachments || []).map((a, i) => ({ name: a.filename || a.url, index: typeof a.index === 'number' ? a.index : i })),
      time: timeOf(m.sentAt),
      rank: 0,
      index: i,
    };
  });
  const notes: ShareItem[] =
    includeNotes && canShareNotes(ticket)
      ? liveNotes(ticket).map((n, i) => ({
          label: 'Nota interna',
          author: n.authorName || 'Operatore',
          sentAt: n.sentAt,
          body: textOf(n.body, isEmailNote(n.authorName)),
          messageId: n._id,
          attachments: (n.attachments || []).map((a, i) => ({ name: a.filename || a.url, index: typeof a.index === 'number' ? a.index : i })),
          time: timeOf(n.sentAt),
          rank: 1,
          index: i,
        }))
      : [];
  // Stesso ordine del thread: data, poi messaggio prima della nota, poi ordine d'origine.
  return [...messages, ...notes].sort((a, b) => a.time - b.time || a.rank - b.rank || a.index - b.index);
};

const pickItems = (items: ShareItem[], content: ShareEmailContent): ShareItem[] => {
  if (content === 'all' || items.length === 0) return items;
  return content === 'first' ? [items[0]] : [items[items.length - 1]];
};

const linkKey = (messageId: string, index: number): string => `${messageId}:${index}`;

const formatItem = (it: ShareItem, links: Map<string, string>): string[] => {
  const lines = [`${it.label} – ${it.author} – ${it.sentAt ? formatDate(it.sentAt) : ''}`.trimEnd()];
  lines.push(it.body || '(nessun testo)');
  if (it.attachments.length > 0) {
    lines.push('Allegati:');
    it.attachments.forEach((a) => {
      const url = it.messageId ? links.get(linkKey(it.messageId, a.index)) : undefined;
      lines.push(url ? `- ${a.name} – ${url}` : `- ${a.name}`);
    });
  }
  return lines;
};

/**
 * Allegati presenti nel contenuto scelto (stessa selezione del testo): quelli
 * per cui chiedere i link al backend. Le note solo con `includeNotes`.
 */
export const shareableAttachments = (
  ticket: ShareEmailTicket,
  options: Pick<ShareEmailOptions, 'content' | 'includeNotes'>
): Array<ShareLinkRequest & { filename: string }> =>
  pickItems(collectItems(ticket, options.includeNotes), options.content).flatMap((it) =>
    it.messageId
      ? it.attachments.map((a) => ({ messageId: it.messageId as string, attachmentId: a.index, filename: a.name }))
      : []
  );

/** Riga finale sulla validita dei link (solo data, ora locale). */
const linksFooter = (expireAt: string | null | undefined, placeholder: boolean): string => {
  if (placeholder) return 'I link agli allegati si aprono senza accesso alla piattaforma (scadenza indicata all’invio).';
  if (!expireAt) return 'I link agli allegati si aprono senza accesso alla piattaforma.';
  const d = new Date(expireAt);
  const when = Number.isNaN(d.getTime()) ? expireAt : d.toLocaleDateString('it-IT');
  return `I link agli allegati si aprono senza accesso alla piattaforma e scadono il ${when}.`;
};

const requesterOf = (ticket: ShareEmailTicket): string => {
  const r = ticket.requester;
  const name = r?.nome || ticket.associateSnapshot?.nominativo || '';
  const email = r?.email || '';
  if (name && email) return `${name} <${email}>`;
  return name || email || '—';
};

const detailLines = (ticket: ShareEmailTicket, origin: string): string[] => {
  const assignees = (ticket.assignees || []).map((a) => a.nome).filter(Boolean);
  const lines = [
    `Ticket #${ticket.ticketNumber || ticket._id}`,
    `Oggetto: ${ticket.subject || '—'}`,
    `Richiedente: ${requesterOf(ticket)}`,
    `Canale: ${ticket.groupId?.nome || '—'}`,
    `Stato: ${ticket.status ? STATUS_LABELS[ticket.status] || ticket.status : '—'}`,
    `Priorità: ${ticket.priority ? PRIORITY_LABELS[ticket.priority] || ticket.priority : '—'}`,
    `Aperto il: ${ticket.createdAt ? formatDate(ticket.createdAt) : '—'}`,
  ];
  lines.push(`Assegnati: ${assignees.length > 0 ? assignees.join(', ') : 'nessuno'}`);
  // Solo i campi compilati; etichette attuali del canale o predefinite (ticket trasferito).
  const pratica = righeDatiPratica(ticket).filter((r) => r.valore);
  if (pratica.length > 0) {
    lines.push('Dati della pratica:');
    pratica.forEach((r) => lines.push(`- ${r.etichetta}: ${r.valore}`.replace(/[\r\n\t]+/g, ' ')));
  }
  lines.push(`Link: ${ticketShareUrl(origin, ticket)}`);
  return lines;
};

/** Corpo dell'email in testo semplice, righe separate da CRLF. */
export const buildShareEmailText = (ticket: ShareEmailTicket, options: ShareEmailOptions): string => {
  const blocks: string[][] = [];
  if (options.includeDetails) blocks.push(detailLines(ticket, options.origin));
  const items = pickItems(collectItems(ticket, options.includeNotes), options.content);
  const links = new Map((options.links || []).map((l) => [linkKey(l.messageId, l.attachmentId), l.url]));
  if (options.linksPlaceholder) {
    items.forEach((it) => {
      if (it.messageId) it.attachments.forEach((a) => links.set(linkKey(it.messageId as string, a.index), LINK_PLACEHOLDER));
    });
  }
  if (items.length === 0) blocks.push(['(nessun messaggio)']);
  items.forEach((it) => blocks.push(formatItem(it, links)));
  const linked = items.some((it) => it.messageId && it.attachments.some((a) => links.has(linkKey(it.messageId as string, a.index))));
  if (linked) blocks.push([linksFooter(options.linksExpireAt, Boolean(options.linksPlaceholder))]);
  const sections = blocks.map((b) => b.join('\n'));
  const head = options.includeDetails ? [sections.shift() as string, SEPARATOR] : [];
  return [...head, sections.join('\n\n')]
    .join('\n\n')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .join(CRLF);
};

export const mailtoHref = (subject: string, body: string): string =>
  `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

export const isLongForMailto = (body: string): boolean => body.length > MAILTO_SOFT_LIMIT;

// ---------------------------------------------------------------------------
// Scelta del programma di posta («Apri con»).
// - `default`: il `mailto:` di sempre (programma impostato nel sistema).
// - `outlook`: Outlook sul web per account di lavoro o scuola (Microsoft 365),
//   deeplink `https://outlook.office.com/mail/deeplink/compose`.
// - `outlookcom`: Outlook.com per account personali (Hotmail, Live, Outlook.com),
//   deeplink `https://outlook.live.com/mail/0/deeplink/compose`.
// - `gmail`: composizione di Gmail `https://mail.google.com/mail/?view=cm&fs=1`.
// I deeplink di Outlook non hanno una documentazione ufficiale Microsoft (sono
// quelli prodotti dalla stessa Outlook sul web); Yahoo e le webmail italiane
// non hanno un URL di composizione documentato: non ci sono.
// Valori codificati con encodeURIComponent: spazi %20 (Outlook mostrerebbe i
// «+»), «+» %2B, «&» %26, a capo %0D%0A, accenti in UTF-8.
// ---------------------------------------------------------------------------
export type EmailProgram = 'default' | 'outlook' | 'outlookcom' | 'gmail';

export const EMAIL_PROGRAMS: Array<{ value: EmailProgram; label: string; action: string }> = [
  { value: 'default', label: 'Programma predefinito (Outlook, Thunderbird, Mail…)', action: 'Apri nel programma di posta' },
  { value: 'outlook', label: 'Outlook sul web – account di lavoro o scuola', action: 'Apri in Outlook' },
  { value: 'outlookcom', label: 'Outlook.com – account personale', action: 'Apri in Outlook.com' },
  { value: 'gmail', label: 'Gmail', action: 'Apri in Gmail' },
];

export const isEmailProgram = (v: unknown): v is EmailProgram =>
  typeof v === 'string' && EMAIL_PROGRAMS.some((p) => p.value === v);

/**
 * Lunghezza massima dell'URL di composizione delle webmail (prudente: oltre, i
 * server rispondono con un errore o tagliano). Il `mailto:` resta governato da
 * MAILTO_SOFT_LIMIT sul corpo, come prima.
 */
export const COMPOSE_URL_LIMITS: Record<Exclude<EmailProgram, 'default'>, number> = {
  outlook: 2000,
  outlookcom: 2000,
  gmail: 6000,
};

const enc = encodeURIComponent;

/** URL di composizione per il programma scelto (corpo vuoto = solo l'oggetto). */
export const composeHref = (program: EmailProgram, subject: string, body: string): string => {
  const bodyPart = body ? `&body=${enc(body)}` : '';
  switch (program) {
    case 'outlook':
      return `https://outlook.office.com/mail/deeplink/compose?subject=${enc(subject)}${bodyPart}`;
    case 'outlookcom':
      return `https://outlook.live.com/mail/0/deeplink/compose?subject=${enc(subject)}${bodyPart}`;
    case 'gmail':
      return `https://mail.google.com/mail/?view=cm&fs=1&su=${enc(subject)}${bodyPart}`;
    default:
      return mailtoHref(subject, body);
  }
};

/** Il testo entra nell'URL del programma scelto (per il mailto: il limite di sempre sul corpo). */
export const fitsCompose = (program: EmailProgram, subject: string, body: string): boolean =>
  program === 'default'
    ? !isLongForMailto(body)
    : composeHref(program, subject, body).length <= COMPOSE_URL_LIMITS[program];
